/**
 * Fotocamera dentro la pagina per fotografare il documento.
 *
 * La foto non finisce nella galleria del telefono: resta in memoria e si
 * scarta dopo la lettura. Per avvicinarsi alla qualita' dell'app Fotocamera:
 *  - si chiede il video alla risoluzione piu' alta (fino a 4K) e con la messa
 *    a fuoco continua, dove il telefono lo permette;
 *  - allo scatto si chiede una vera foto (ImageCapture, Chrome su Android):
 *    piena risoluzione e messa a fuoco come l'app Fotocamera;
 *  - dove non c'e' (iPhone, alcuni browser) si prendono alcuni fotogrammi del
 *    video e si tiene il piu' nitido, cosi' il mosso del tocco non conta.
 * Si tiene solo la zona della cornice, con un po' di margine.
 */

/** Margine attorno alla cornice: dal video e dalla foto vera (meno precisa). */
const MARGINE_VIDEO = 0.15;
const MARGINE_FOTO = 0.25;

const attendi = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Apre la fotocamera posteriore alla risoluzione piu' alta disponibile. */
export async function apriFlusso() {
  const flusso = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: { facingMode: { ideal: "environment" }, width: { ideal: 3840 }, height: { ideal: 2160 } }
  });
  const traccia = flusso.getVideoTracks()[0];
  try {
    const possibili = traccia && traccia.getCapabilities ? traccia.getCapabilities() : {};
    if (possibili.focusMode && possibili.focusMode.includes("continuous")) {
      await traccia.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
    }
  } catch (e) {
    // La messa a fuoco resta quella del telefono.
  }
  return flusso;
}

/**
 * Zona del video (in pixel della fotocamera) dentro la cornice, piu' un
 * margine. Il video e' mostrato "a riempimento" (object-fit: cover), centrato.
 */
export function zonaCornice(video, cornice, margine = MARGINE_VIDEO) {
  const W = video.videoWidth;
  const H = video.videoHeight;
  const tutto = { x: 0, y: 0, w: W, h: H };
  if (!cornice) {
    return tutto;
  }
  const rv = video.getBoundingClientRect();
  const rc = cornice.getBoundingClientRect();
  const s = Math.max(rv.width / W, rv.height / H);
  const dx = (rv.width - W * s) / 2;
  const dy = (rv.height - H * s) / 2;
  const x = Math.max(0, Math.round((rc.left - rv.left - dx - rc.width * margine) / s));
  const y = Math.max(0, Math.round((rc.top - rv.top - dy - rc.height * margine) / s));
  const w = Math.min(W, Math.round((rc.right - rv.left - dx + rc.width * margine) / s)) - x;
  const h = Math.min(H, Math.round((rc.bottom - rv.top - dy + rc.height * margine) / s)) - y;
  return w > 100 && h > 100 ? { x, y, w, h } : tutto;
}

/** Copia di una zona di un'immagine (o del video) in un canvas. */
function ritaglia(sorgente, zona) {
  const c = document.createElement("canvas");
  c.width = Math.round(zona.w);
  c.height = Math.round(zona.h);
  c.getContext("2d").drawImage(sorgente, zona.x, zona.y, zona.w, zona.h, 0, 0, c.width, c.height);
  return c;
}

/** Vera foto dalla fotocamera (ImageCapture), null dove non si puo'. */
async function fotoVera(traccia) {
  if (!traccia || typeof window.ImageCapture !== "function") {
    return null;
  }
  try {
    const cattura = new window.ImageCapture(traccia);
    const opzioni = {};
    try {
      // Niente flash: sulla plastica del documento fa solo riflessi.
      const possibili = await cattura.getPhotoCapabilities();
      if (possibili.fillLightMode && possibili.fillLightMode.includes("off")) {
        opzioni.fillLightMode = "off";
      }
    } catch (e) {
      // Si scatta con le impostazioni del telefono.
    }
    return await cattura.takePhoto(opzioni);
  } catch (e) {
    return null;
  }
}

/**
 * Ritaglia la foto vera sulla cornice. Il video e' il centro della foto con
 * le proporzioni del video; se foto e video hanno orientamento diverso (foto
 * non raddrizzata) si tiene la foto intera: la lettura prova anche a girarla.
 */
async function ritagliaFoto(blob, video, cornice) {
  const foto = await createImageBitmap(blob, { imageOrientation: "from-image" });
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if ((foto.width > foto.height) !== (vw > vh)) {
    return ritaglia(foto, { x: 0, y: 0, w: foto.width, h: foto.height });
  }
  const av = vw / vh;
  const cw = foto.width / foto.height > av ? foto.height * av : foto.width;
  const ch = cw / av;
  const f = cw / vw;
  const ox = (foto.width - cw) / 2;
  const oy = (foto.height - ch) / 2;
  const z = zonaCornice(video, cornice, MARGINE_FOTO);
  const x = Math.max(0, ox + z.x * f);
  const y = Math.max(0, oy + z.y * f);
  return ritaglia(foto, { x, y, w: Math.min(foto.width - x, z.w * f), h: Math.min(foto.height - y, z.h * f) });
}

/** Nitidezza: varianza del laplaciano su una copia piccola in grigi. */
function nitidezza(canvas) {
  const larghezza = Math.min(480, canvas.width);
  const altezza = Math.max(1, Math.round((canvas.height * larghezza) / canvas.width));
  const c = document.createElement("canvas");
  c.width = larghezza;
  c.height = altezza;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(canvas, 0, 0, larghezza, altezza);
  const d = ctx.getImageData(0, 0, larghezza, altezza).data;
  const g = new Float32Array(larghezza * altezza);
  for (let i = 0; i < g.length; i++) {
    g[i] = d[i * 4] * 0.299 + d[i * 4 + 1] * 0.587 + d[i * 4 + 2] * 0.114;
  }
  let somma = 0;
  let somma2 = 0;
  let n = 0;
  for (let y = 1; y < altezza - 1; y++) {
    for (let x = 1; x < larghezza - 1; x++) {
      const i = y * larghezza + x;
      const l = 4 * g[i] - g[i - 1] - g[i + 1] - g[i - larghezza] - g[i + larghezza];
      somma += l;
      somma2 += l * l;
      n++;
    }
  }
  return n ? somma2 / n - (somma / n) ** 2 : 0;
}

/** Alcuni fotogrammi del video in mezzo secondo: si tiene il piu' nitido. */
async function fotogrammaMigliore(video, cornice) {
  const zona = zonaCornice(video, cornice);
  // Il tocco sul pulsante muove il telefono: si aspetta un attimo.
  await attendi(150);
  let migliore = null;
  let punti = -1;
  for (let i = 0; i < 5; i++) {
    const c = ritaglia(video, zona);
    const p = nitidezza(c);
    if (p > punti) {
      migliore = c;
      punti = p;
    }
    await attendi(90);
  }
  return migliore;
}

/**
 * Scatta la foto del documento: vera foto dove si puo', altrimenti il
 * fotogramma piu' nitido del video. Restituisce un Blob JPEG in memoria.
 */
export async function scatta(flusso, video, cornice) {
  const traccia = flusso && flusso.getVideoTracks()[0];
  let canvas = null;
  const blob = await fotoVera(traccia);
  if (blob) {
    try {
      canvas = await ritagliaFoto(blob, video, cornice);
    } catch (e) {
      canvas = null;
    }
  }
  if (!canvas) {
    canvas = await fotogrammaMigliore(video, cornice);
  }
  if (typeof console !== "undefined") {
    console.info("docscan: scatto " + (blob ? "foto" : "video") + " " + canvas.width + "x" + canvas.height + " (video " + video.videoWidth + "x" + video.videoHeight + ")");
  }
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
}

export default { apriFlusso, zonaCornice, scatta };
